/**
 * Assets page - main page for managing gold assets
 */

import { AssetDeleteConfirm } from '@/components/assets/AssetDeleteConfirm'
import { AssetEditModal } from '@/components/assets/AssetEditModal'
import { AssetForm } from '@/components/assets/AssetForm'
import { AssetList } from '@/components/assets/AssetList'
import { SectionErrorBoundary } from '@/components/common/SectionErrorBoundary'
import { SaleForm } from '@/components/sales/SaleForm'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useAssets, useDeleteAsset } from '@/services/api/assets'
import { useGoldPrices } from '@/services/api/goldPrice'
import type { GoldAsset } from '@/types/asset'
import type { GoldPriceHistory } from '@/types/price'
import { IconPlus } from '@tabler/icons-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

export default function Assets() {
  const [showForm, setShowForm] = useState(false)
  const [editingAsset, setEditingAsset] = useState<GoldAsset | null>(null)
  const [deletingAsset, setDeletingAsset] = useState<GoldAsset | null>(null)
  const [sellingAsset, setSellingAsset] = useState<GoldAsset | null>(null)
  const [statusFilter, setStatusFilter] = useState<'holding' | 'sold' | undefined>(undefined)

  const { data: assets = [], isLoading, refetch } = useAssets(statusFilter)
  const { data: currentPrices = [] } = useGoldPrices('Hồ Chí Minh')
  const deleteMutation = useDeleteAsset()

  // Create map of latest prices by gold type ID
  const latestPrices = useMemo(() => {
    const priceMap = new Map<number, GoldPriceHistory>()
    const now = new Date().toISOString()
    const today = new Date().toISOString().split('T')[0] as string

    currentPrices.forEach(price => {
      priceMap.set(price.Id, {
        id: `current_${price.Id}`,
        date: today,
        goldTypeId: price.Id,
        branchName: price.BranchName,
        buyPrice: price.BuyValue,
        sellPrice: price.SellValue,
        createdAt: now,
      })
    })
    return priceMap
  }, [currentPrices])

  const handleEdit = (asset: GoldAsset) => {
    setEditingAsset(asset)
  }

  const handleDelete = (asset: GoldAsset) => {
    setDeletingAsset(asset)
  }

  const handleDeleteConfirm = async () => {
    if (!deletingAsset) return

    try {
      await deleteMutation.mutateAsync(deletingAsset.id)
      setDeletingAsset(null)
      refetch()
      toast.success('Đã xóa tài sản thành công')
    } catch (error) {
      console.error('Failed to delete asset:', error)
      toast.error('Không thể xóa tài sản. Có thể tài sản này có giao dịch bán liên quan.')
    }
  }

  const handleFormSuccess = () => {
    setShowForm(false)
    refetch()
    toast.success('Đã tạo tài sản thành công')
  }

  const handleFormOpenChange = (open: boolean) => {
    setShowForm(open)
  }

  const handleEditSuccess = () => {
    setEditingAsset(null)
    refetch()
    toast.success('Đã cập nhật tài sản thành công')
  }

  const handleSellSuccess = () => {
    setSellingAsset(null)
    refetch()
    toast.success('Đã ghi nhận giao dịch bán')
  }

  return (
    <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 lg:py-8">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Quản lý Tài sản Vàng</h1>
        <Button onClick={() => setShowForm(true)} className="w-full sm:w-auto min-h-[44px]">
          <IconPlus className="h-4 w-4 mr-2" stroke={1.5} />
          Thêm tài sản
        </Button>
      </div>

      <div className="mb-4 sm:mb-6 flex flex-wrap gap-2">
        <Button
          onClick={() => setStatusFilter(undefined)}
          variant={statusFilter === undefined ? 'default' : 'outline'}
          size="sm"
          className="min-h-[44px] flex-1 sm:flex-initial"
        >
          Tất cả
        </Button>
        <Button
          onClick={() => setStatusFilter('holding')}
          variant={statusFilter === 'holding' ? 'default' : 'outline'}
          size="sm"
          className="min-h-[44px] flex-1 sm:flex-initial"
        >
          Đang nắm giữ
        </Button>
        <Button
          onClick={() => setStatusFilter('sold')}
          variant={statusFilter === 'sold' ? 'default' : 'outline'}
          size="sm"
          className="min-h-[44px] flex-1 sm:flex-initial"
        >
          Đã bán
        </Button>
      </div>

      <SectionErrorBoundary sectionName="Danh sách tài sản">
        <AssetList
          assets={assets}
          currentPrices={latestPrices}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onSell={asset => setSellingAsset(asset)}
          isLoading={isLoading}
        />
      </SectionErrorBoundary>

      <Dialog open={showForm} onOpenChange={handleFormOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Thêm tài sản mới</DialogTitle>
          </DialogHeader>
          <AssetForm onSuccess={handleFormSuccess} onCancel={() => setShowForm(false)} />
        </DialogContent>
      </Dialog>

      {editingAsset && (
        <AssetEditModal
          asset={editingAsset}
          isOpen={!!editingAsset}
          onClose={() => setEditingAsset(null)}
          onSuccess={handleEditSuccess}
        />
      )}

      {deletingAsset && (
        <AssetDeleteConfirm
          asset={deletingAsset}
          isOpen={!!deletingAsset}
          onClose={() => setDeletingAsset(null)}
          onConfirm={handleDeleteConfirm}
          isDeleting={deleteMutation.isPending}
        />
      )}

      {sellingAsset && (
        <Dialog open={!!sellingAsset} onOpenChange={open => !open && setSellingAsset(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Bán tài sản: {sellingAsset.name}</DialogTitle>
            </DialogHeader>
            <SaleForm
              defaultAssetId={sellingAsset.id}
              onSuccess={handleSellSuccess}
              onCancel={() => setSellingAsset(null)}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
